/**
 * Jeep Sing Fashion - MapLibre GL MOE School Locator
 * Uses MapLibre GL JS v4.7.1 with OpenFreeMap Liberty vector tiles (similar to hotelist.com).
 * Includes Singapore Open Data Licence attribution (https://data.gov.sg/open-data-licence).
 */

(function () {
  'use strict';

  // Constants & Config
  const SHOWROOM = {
    name: 'Jeep Sing Fashion Showroom & HQ',
    address: '4012 Ang Mo Kio Ave 10, Techplace 1 #01-09, Singapore 569628',
    postal: '569628',
    phone: '+65 6456 3198',
    hours: 'Mon - Fri: 10:00am - 6:00pm, Sat: 10:00am - 2:00pm',
    lat: 1.3609735,
    lng: 103.8567265
  };

  // MapLibre uses [lng, lat] coordinate order
  const SG_CENTER = [103.8198, 1.3521];
  const DEFAULT_ZOOM = window.innerWidth < 768 ? 10.8 : 11.8;
  const MAP_STYLE = 'https://tiles.openfreemap.org/styles/liberty';

  // Same rule for MOE names and setting keys: "CHUNG CHENG HIGH SCHOOL (MAIN)" -> "chung-cheng-high-school-main"
  const toSlug = (str) => String(str || '')
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  const escapeHtml = (str) => String(str ?? '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]));

  const sanitizeUrl = (url) => {
    if (!url || typeof url !== 'string') return '#';
    const trimmed = url.trim();
    if (/^(?:https?:|\/|tel:|mailto:)/i.test(trimmed)) {
      return encodeURI(trimmed);
    }
    return '#';
  };

  class SchoolMapLocator {
    constructor(container) {
      this.container = container;
      this.mapEl = container.querySelector('#ds-osm-map');
      this.listEl = container.querySelector('#ds-school-list');
      this.searchInput = container.querySelector('#ds-school-search');
      this.searchClear = container.querySelector('#ds-search-clear');
      this.countEl = container.querySelector('#ds-school-count');
      this.geoBtn = container.querySelector('#ds-btn-geo');
      this.zoneChips = container.querySelectorAll('[data-filter-zone]');
      this.levelChips = container.querySelectorAll('[data-filter-level]');
      this.stockChips = container.querySelectorAll('[data-filter-stock]');

      // Mobile responsive elements
      this.mobileToggle = container.querySelector('#ds-mobile-view-toggle');
      this.mobileCount = container.querySelector('#ds-mobile-count');
      this.mobileSheet = container.querySelector('#ds-mobile-school-sheet');
      this.mobileSheetContent = container.querySelector('#ds-mobile-sheet-content');
      this.mobileSheetClose = container.querySelector('#ds-mobile-sheet-close');
      this.filterToggleBtn = container.querySelector('#ds-btn-filter-toggle');
      this.filtersContainer = container.querySelector('#ds-school-filters');
      this.isMobileListView = false;

      this.schools = [];
      this.filteredSchools = [];
      this.markersMap = new Map(); // school.id -> { marker, popup, data }
      this.selectedSchoolId = null; // clicked & locked
      this.hoveredSchoolId = null;  // transient hover
      this.hoverLeaveTimer = null;
      this.hoverDebounce = null;
      this.activeSchoolId = null;
      this.userLocation = null;
      this.userMarker = null;

      this.filters = {
        query: '',
        zone: 'ALL',
        level: 'ALL',
        stock: 'ALL', // 'ALL', 'STOCKED'
        sortBy: 'distance' // 'distance', 'user'
      };

      this.init();
    }

    async init() {
      // 1. Ensure MapLibre GL JS is loaded
      await this.loadMapLibre();

      // 2. Load Schools Data
      await this.loadData();
      this.attachShopCollections();

      // 3. Initialize MapLibre Map
      this.initMap();

      // 4. Bind UI Event Handlers
      this.bindEvents();

      // 5. Initial Render
      this.applyFilters();
    }

    loadMapLibre() {
      return new Promise((resolve) => {
        if (window.maplibregl) return resolve();

        const cssUrl = this.container.dataset.maplibreCss || '/assets/maplibre-gl.css';
        const jsUrl = this.container.dataset.maplibreJs || '/assets/maplibre-gl.js';

        // Inject MapLibre CSS
        if (!document.querySelector('link[href*="maplibre-gl"]')) {
          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = cssUrl;
          document.head.appendChild(link);
        }

        // Inject MapLibre JS
        const script = document.createElement('script');
        script.src = jsUrl;
        script.onload = () => resolve();
        script.onerror = () => {
          console.warn('[SchoolMap] Failed to load local MapLibre script, falling back:', jsUrl);
          resolve();
        };
        document.head.appendChild(script);
      });
    }

    initMap() {
      if (!this.mapEl || !window.maplibregl) return;

      this.map = new maplibregl.Map({
        container: this.mapEl,
        style: MAP_STYLE,
        center: SG_CENTER,
        zoom: DEFAULT_ZOOM,
        minZoom: 9.5,
        maxZoom: 18,
        pitch: 18, // 3D tilt perspective like hotelist.com
        bearing: 0,
        antialias: true,
        attributionControl: false // Custom attribution added below
      });

      // Add Custom Attribution with Singapore Open Data Licence and OpenStreetMap
      this.map.addControl(new maplibregl.AttributionControl({
        compact: true,
        customAttribution: [
          'Contains info from <a href="https://data.gov.sg/datasets/d_688b934f82c1059ed0a6993d2a829089/view" target="_blank" rel="noopener">MOE Schools</a> under <a href="https://data.gov.sg/open-data-licence" target="_blank" rel="noopener">Singapore Open Data Licence v1.0</a>',
          '&copy; <a href="https://openfreemap.org" target="_blank" rel="noopener">OpenFreeMap</a> &copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>'
        ]
      }), 'bottom-right');

      // Add Zoom, Pitch, and Compass controls
      this.map.addControl(new maplibregl.NavigationControl({
        visualizePitch: true,
        showCompass: true
      }), 'top-right');

      this.map.on('load', () => {
        // Setup Singapore MRT Rail Network
        this.setupMrtLayers();
        // Setup MOE Priority Radius Layer
        this.setupRadiusLayers();
        // Add Jeep Sing Fashion Showroom Pin
        this.addShowroomMarker();
        // Render school markers once map style is ready
        this.renderMarkers();
      });

      // Dismiss mobile bottom sheet & clear selection when user taps on empty map space
      this.map.on('click', (e) => {
        const isMarker = e.originalEvent && e.originalEvent.target && e.originalEvent.target.closest('.ds-osm-marker');
        const isPopup = e.originalEvent && e.originalEvent.target && e.originalEvent.target.closest('.maplibregl-popup');
        if (!isMarker && !isPopup) {
          this.closeMobileSheet();
          this.clearSelection();
        }
      });
    }

    addShowroomMarker() {
      const el = document.createElement('div');
      el.className = 'ds-osm-marker ds-osm-marker--showroom';
      el.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="#ffde59"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>';
      el.setAttribute('title', escapeHtml(SHOWROOM.name));

      const cleanPhone = SHOWROOM.phone.replace(/\s+/g, '');
      const showroomPopupHtml = `
        <div class="ds-school-popup-card ds-school-popup-card--showroom">
          <div class="ds-school-popup-card__header">
            <div class="ds-school-popup-card__eyebrow">
              <span class="ds-school-popup-badge" style="background:#b60000;color:#fff;">HEADQUARTERS & SHOWROOM</span>
              <span class="ds-school-popup-badge" style="background:rgba(255,222,89,0.18);color:#ffde59;border:1px solid rgba(255,222,89,0.4);">★ SINCE 1963</span>
            </div>
            <h3 class="ds-school-popup-card__title" style="color:#ffffff;">${escapeHtml(SHOWROOM.name)}</h3>
            <div class="ds-school-popup-card__address">📍 ${escapeHtml(SHOWROOM.address)}</div>
          </div>

          <div class="ds-school-popup-card__metrics">
            <div class="ds-school-popup-metric-row">
              <span class="ds-school-popup-metric-label">Opening Hours:</span>
              <span class="ds-school-popup-metric-value">${escapeHtml(SHOWROOM.hours)}</span>
            </div>
            <div class="ds-school-popup-metric-row">
              <span class="ds-school-popup-metric-label">Direct Hotline:</span>
              <span class="ds-school-popup-metric-value"><a href="tel:${cleanPhone}" style="color:#38bdf8;text-decoration:underline;">${escapeHtml(SHOWROOM.phone)}</a></span>
            </div>
          </div>

          <div class="ds-school-popup-card__actions">
            <a href="https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(SHOWROOM.address)}" target="_blank" rel="noopener" class="ds-school-popup-btn ds-school-popup-btn--product" style="background:#b60000;color:#fff;">
              🚗 Get Directions to Showroom
            </a>
          </div>

          <div class="ds-school-popup-card__footer">
            <span>Jeep Sing Fashion Main Showroom & Distribution Centre</span>
          </div>
        </div>
      `;

      this.showroomPopup = new maplibregl.Popup({
        offset: 25,
        closeButton: true,
        closeOnClick: false,
        maxWidth: '340px'
      }).setHTML(showroomPopupHtml);

      this.showroomPopup.on('close', () => {
        if (this.showroomMarker) {
          this.showroomMarker.getElement().classList.remove('is-active');
        }
      });

      el.addEventListener('click', (e) => {
        e.stopPropagation();
        e.preventDefault();
        this.clearSchoolRadiusCircle();
        this.selectedSchoolId = null;
        this.markersMap.forEach(({ marker, popup }) => {
          marker.getElement().classList.remove('is-active');
          if (popup && popup.isOpen()) popup.remove();
        });
        if (this.listEl) {
          this.listEl.querySelectorAll('.ds-school-card').forEach((c) => c.classList.remove('is-selected', 'is-hovered'));
        }
        if (this.showroomMarker) {
          this.showroomMarker.getElement().classList.add('is-active');
        }

        if (window.innerWidth <= 768) {
          if (this.showroomPopup.isOpen()) this.showroomPopup.remove();
          if (this.mobileSheet && this.mobileSheetContent) {
            this.mobileSheetContent.innerHTML = showroomPopupHtml;
            this.mobileSheet.classList.add('is-open');
            this.mobileSheet.setAttribute('aria-hidden', 'false');
          }
          this.map.flyTo({
            center: [SHOWROOM.lng, SHOWROOM.lat],
            offset: [0, -80],
            zoom: 15.5,
            pitch: 20,
            speed: 1.2,
            curve: 1.4,
            essential: true
          });
        } else {
          this.showroomPopup.setLngLat([SHOWROOM.lng, SHOWROOM.lat]).addTo(this.map);
          this.map.flyTo({
            center: [SHOWROOM.lng, SHOWROOM.lat],
            zoom: 15.5,
            pitch: 28,
            speed: 1.2,
            curve: 1.4,
            essential: true
          });
        }
      });

      el.addEventListener('mouseenter', () => {
        if (window.innerWidth > 768 && !this.selectedSchoolId) {
          this.clearSchoolRadiusCircle();
          this.markersMap.forEach(({ marker, popup }) => {
            marker.getElement().classList.remove('is-active');
            if (popup.isOpen()) popup.remove();
          });
          if (!this.showroomPopup.isOpen()) {
            this.showroomPopup.setLngLat([SHOWROOM.lng, SHOWROOM.lat]).addTo(this.map);
          }
        }
      });

      this.showroomMarker = new maplibregl.Marker({ element: el })
        .setLngLat([SHOWROOM.lng, SHOWROOM.lat])
        .addTo(this.map);
    }

    /**
     * Singapore MRT & LRT Rail Network Layers
     * Renders official LTA MRT/LRT lines and station nodes using MapLibre GeoJSON layers.
     */
    async setupMrtLayers() {
      if (!this.map) return;
      const mrtUrl = this.container.dataset.mrtUrl || '/assets/sg-mrt-lines.geojson';

      try {
        const res = await fetch(mrtUrl);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        if (this.map.getSource('mrt-rail-source')) return;

        this.map.addSource('mrt-rail-source', {
          type: 'geojson',
          data: data
        });

        // Insert before radius fill if radius layer already exists
        const beforeLayerId = this.map.getLayer('school-radius-fill') ? 'school-radius-fill' : undefined;

        // 1. Rail Line Casing (High contrast outer border)
        this.map.addLayer({
          id: 'mrt-lines-casing',
          type: 'line',
          source: 'mrt-rail-source',
          filter: ['==', ['get', 'type'], 'line'],
          paint: {
            'line-color': '#ffffff',
            'line-width': [
              'interpolate', ['linear'], ['zoom'],
              10, 2.5,
              14, 5.5,
              18, 9
            ],
            'line-opacity': 0.85
          }
        }, beforeLayerId);

        // 2. Rail Line Main Stroke (Official LTA line color)
        this.map.addLayer({
          id: 'mrt-lines-stroke',
          type: 'line',
          source: 'mrt-rail-source',
          filter: ['==', ['get', 'type'], 'line'],
          paint: {
            'line-color': ['coalesce', ['get', 'color'], '#005ec4'],
            'line-width': [
              'interpolate', ['linear'], ['zoom'],
              10, 1.8,
              14, 3.8,
              18, 6.5
            ],
            'line-opacity': 0.95
          }
        }, beforeLayerId);

        // 3. Station Point Circles
        this.map.addLayer({
          id: 'mrt-stations-outer',
          type: 'circle',
          source: 'mrt-rail-source',
          filter: ['==', ['get', 'type'], 'station'],
          minzoom: 11,
          paint: {
            'circle-radius': [
              'interpolate', ['linear'], ['zoom'],
              11, 2.5,
              13, 4,
              16, 6
            ],
            'circle-color': '#ffffff',
            'circle-stroke-color': '#141f56',
            'circle-stroke-width': [
              'interpolate', ['linear'], ['zoom'],
              11, 1,
              14, 1.5,
              16, 2
            ],
            'circle-opacity': 0.95
          }
        }, beforeLayerId);

        // 4. Station Text Labels
        this.map.addLayer({
          id: 'mrt-stations-label',
          type: 'symbol',
          source: 'mrt-rail-source',
          filter: ['==', ['get', 'type'], 'station'],
          minzoom: 13,
          layout: {
            'text-field': ['get', 'name'],
            'text-font': ['Noto Sans Regular'],
            'text-size': [
              'interpolate', ['linear'], ['zoom'],
              13, 10,
              16, 12
            ],
            'text-offset': [0, 1],
            'text-anchor': 'top',
            'text-optional': true
          },
          paint: {
            'text-color': '#141f56',
            'text-halo-color': '#ffffff',
            'text-halo-width': 1.5
          }
        }, beforeLayerId);

        // MRT Hover Tooltip
        const mrtTooltip = new maplibregl.Popup({
          closeButton: false,
          closeOnClick: false,
          offset: 8,
          className: 'ds-mrt-tooltip-popup'
        });

        this.map.on('mouseenter', 'mrt-stations-outer', (e) => {
          this.map.getCanvas().style.cursor = 'pointer';
          if (!e.features || !e.features[0]) return;
          const feat = e.features[0];
          const props = feat.properties || {};
          const coords = feat.geometry.coordinates.slice();
          const codeBadge = props.station_codes ? `<span class="ds-mrt-tooltip__code">${props.station_codes}</span>` : '';
          mrtTooltip.setLngLat(coords)
            .setHTML(`<div class="ds-mrt-tooltip"><span class="ds-mrt-tooltip__name">🚇 ${props.name || 'Station'}</span>${codeBadge}</div>`)
            .addTo(this.map);
        });

        this.map.on('mouseleave', 'mrt-stations-outer', () => {
          this.map.getCanvas().style.cursor = '';
          mrtTooltip.remove();
        });

        this.map.on('mouseenter', 'mrt-lines-stroke', (e) => {
          this.map.getCanvas().style.cursor = 'pointer';
          if (!e.features || !e.features[0]) return;
          const feat = e.features[0];
          const props = feat.properties || {};
          if (props.name) {
            mrtTooltip.setLngLat(e.lngLat)
              .setHTML(`<div class="ds-mrt-tooltip ds-mrt-tooltip--line" style="border-left: 3px solid ${props.color || '#005ec4'};"><span class="ds-mrt-tooltip__name">🚆 ${props.name}</span></div>`)
              .addTo(this.map);
          }
        });

        this.map.on('mouseleave', 'mrt-lines-stroke', () => {
          this.map.getCanvas().style.cursor = '';
          mrtTooltip.remove();
        });
      } catch (err) {
        console.warn('[SchoolMap] MRT rail network layer not loaded:', err);
      }
    }

    /**
     * MOE Primary 1 Priority Radius GeoJSON Layers
     * Renders concentric < 1km and 1-2km priority zones strictly for Primary Schools.
     */
    setupRadiusLayers() {
      if (!this.map) return;

      this.map.addSource('school-radius-source', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] }
      });

      this.map.addLayer({
        id: 'school-radius-fill',
        type: 'fill',
        source: 'school-radius-source',
        paint: {
          'fill-color': ['coalesce', ['get', 'fillColor'], ['get', 'color'], '#1e60d5'],
          'fill-opacity': ['coalesce', ['get', 'fillOpacity'], 0.12]
        }
      });

      this.map.addLayer({
        id: 'school-radius-stroke',
        type: 'line',
        source: 'school-radius-source',
        paint: {
          'line-color': ['coalesce', ['get', 'strokeColor'], ['get', 'color'], '#1e60d5'],
          'line-width': ['coalesce', ['get', 'strokeWidth'], 2],
          'line-dasharray': [3, 2]
        }
      });
    }

    updateSchoolRadiusCircle(school) {
      if (!this.map || !this.map.getSource('school-radius-source') || !school.lng || !school.lat) return;

      // Distance priority zones (1km & 1-2km) are strictly applicable to Primary Schools in Singapore
      if (school.level_group !== 'Primary') {
        this.clearSchoolRadiusCircle();
        return;
      }

      const center = [school.lng, school.lat];

      // 1. Outer Ring: 2.0 km (1-2 km Approximate Distance Guide)
      const circle2km = this.createGeoJSONCircle(center, 2.0, {
        color: '#2563eb',
        fillColor: '#3b82f6',
        fillOpacity: 0.05,
        strokeColor: '#3b82f6',
        strokeWidth: 1.5,
        zoneName: '1-2 km Radius (Approximate guide)'
      });

      // 2. Inner Ring: 1.0 km (< 1 km Approximate Distance Guide)
      const circle1km = this.createGeoJSONCircle(center, 1.0, {
        color: '#1d4ed8',
        fillColor: '#1d4ed8',
        fillOpacity: 0.14,
        strokeColor: '#1d4ed8',
        strokeWidth: 2.2,
        zoneName: '< 1 km Radius (Approximate guide)'
      });

      this.map.getSource('school-radius-source').setData({
        type: 'FeatureCollection',
        features: [circle2km, circle1km]
      });
    }

    clearSchoolRadiusCircle() {
      if (!this.map || !this.map.getSource('school-radius-source')) return;

      this.map.getSource('school-radius-source').setData({
        type: 'FeatureCollection',
        features: []
      });
    }

    createGeoJSONCircle(center, radiusKm, options = {}, points = 64) {
      const lng = center[0];
      const lat = center[1];
      const coords = [];
      const distanceX = radiusKm / (111.320 * Math.cos(lat * Math.PI / 180));
      const distanceY = radiusKm / 110.574;

      for (let i = 0; i < points; i++) {
        const theta = (i / points) * (2 * Math.PI);
        const x = distanceX * Math.cos(theta);
        const y = distanceY * Math.sin(theta);
        coords.push([lng + x, lat + y]);
      }
      coords.push(coords[0]); // close polygon loop

      const color = typeof options === 'string' ? options : (options.color || '#1e60d5');
      const fillColor = options.fillColor || color;
      const fillOpacity = options.fillOpacity !== undefined ? options.fillOpacity : 0.12;
      const strokeColor = options.strokeColor || color;
      const strokeWidth = options.strokeWidth || 2;
      const zoneName = options.zoneName || `${radiusKm} km Radius`;

      return {
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: [coords]
        },
        properties: {
          radiusKm: radiusKm,
          color: color,
          fillColor: fillColor,
          fillOpacity: fillOpacity,
          strokeColor: strokeColor,
          strokeWidth: strokeWidth,
          zoneName: zoneName
        }
      };
    }

    async loadData() {
      // 1. Check for inline data script
      const inlineData = document.getElementById('ds-moe-schools-data');
      if (inlineData) {
        try {
          const parsed = JSON.parse(inlineData.textContent);
          this.schools = parsed.schools || parsed;
          return;
        } catch (e) {
          console.warn('[SchoolMap] Failed to parse inline data:', e);
        }
      }

      // 2. Fetch from Shopify theme asset
      const assetUrl = this.container.dataset.dataUrl || '/assets/moe-schools.json';
      try {
        const res = await fetch(assetUrl);
        const data = await res.json();
        this.schools = data.schools || data;
      } catch (err) {
        console.error('[SchoolMap] Failed to fetch school data:', err);
      }
    }

    /**
     * Tags each school with its uniform collection (school.shop) from the
     * section's school_collections setting, rendered as JSON by Liquid.
     */
    attachShopCollections() {
      const el = this.container.querySelector('[data-school-collections]');
      let entries = [];
      try {
        entries = el ? JSON.parse(el.textContent) : [];
      } catch (e) {
        console.warn('[SchoolMap] Failed to parse school collections:', e);
      }

      const bySlug = new Map();
      (Array.isArray(entries) ? entries : []).forEach((entry) => {
        if (!entry?.handle || !entry?.url) return;
        bySlug.set(toSlug(entry.school || entry.handle), entry);
      });

      this.schools.forEach((school) => {
        school.shop = bySlug.get(toSlug(school.name)) || null;
      });
    }

    bindEvents() {
      // Search Input
      let debounceTimer;
      this.searchInput.addEventListener('input', (e) => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          this.filters.query = e.target.value.trim().toLowerCase();
          if (this.searchClear) {
            this.searchClear.classList.toggle('is-visible', this.filters.query.length > 0);
          }
          this.applyFilters();
        }, 180);
      });

      if (this.searchClear) {
        this.searchClear.addEventListener('click', () => {
          this.searchInput.value = '';
          this.filters.query = '';
          this.searchClear.classList.remove('is-visible');
          this.applyFilters();
          this.searchInput.focus();
        });
      }

      // Zone Filter Chips
      this.zoneChips.forEach((chip) => {
        chip.addEventListener('click', () => {
          this.zoneChips.forEach((c) => c.classList.remove('is-active'));
          chip.classList.add('is-active');
          this.filters.zone = chip.dataset.filterZone;
          this.applyFilters();
        });
      });

      // Level Filter Chips
      this.levelChips.forEach((chip) => {
        chip.addEventListener('click', () => {
          this.levelChips.forEach((c) => c.classList.remove('is-active'));
          chip.classList.add('is-active');
          this.filters.level = chip.dataset.filterLevel;
          this.applyFilters();
        });
      });

      // Uniform Availability Chips
      this.stockChips.forEach((chip) => {
        chip.addEventListener('click', () => {
          this.stockChips.forEach((c) => c.classList.remove('is-active'));
          chip.classList.add('is-active');
          this.filters.stock = chip.dataset.filterStock;
          this.applyFilters();
        });
      });

      // Geolocation "Near Me"
      if (this.geoBtn) {
        this.geoBtn.addEventListener('click', () => this.handleGeolocation());
      }

      // Mobile Filter Toggle Button
      if (this.filterToggleBtn && this.filtersContainer) {
        this.filterToggleBtn.addEventListener('click', () => {
          const isExpanded = this.filtersContainer.classList.toggle('is-expanded');
          this.filterToggleBtn.classList.toggle('is-active', isExpanded);
        });
      }

      // Mobile View Switcher (List <-> Map)
      if (this.mobileToggle) {
        this.mobileToggle.addEventListener('click', () => {
          this.toggleMobileView();
        });
      }

      // Mobile Bottom Sheet Close Button
      if (this.mobileSheetClose) {
        this.mobileSheetClose.addEventListener('click', () => {
          this.closeMobileSheet();
        });
      }
    }

    handleGeolocation() {
      if (!navigator.geolocation) {
        alert('Geolocation is not supported by your browser.');
        return;
      }

      this.geoBtn.textContent = '⏳ Locating...';
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          this.userLocation = {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude
          };
          this.geoBtn.textContent = '📍 Near Me (Active)';
          this.geoBtn.style.background = '#eef2ff';
          this.geoBtn.style.borderColor = '#1e2d7d';

          // Add / update user marker in MapLibre
          if (!this.userMarker) {
            const userEl = document.createElement('div');
            userEl.className = 'ds-user-location-pin';
            userEl.innerHTML = '<div style="width:14px;height:14px;background:#2563eb;border:3px solid #fff;border-radius:50%;box-shadow:0 0 10px #2563eb;"></div>';

            this.userMarker = new maplibregl.Marker({ element: userEl })
              .setLngLat([this.userLocation.lng, this.userLocation.lat])
              .setPopup(new maplibregl.Popup({ offset: 15 }).setHTML('<b>You are here</b>'))
              .addTo(this.map);
          } else {
            this.userMarker.setLngLat([this.userLocation.lng, this.userLocation.lat]);
          }

          // Calculate distance to user for all schools
          this.schools.forEach((s) => {
            if (s.lat && s.lng) {
              s.distance_to_user_km = this.haversine(this.userLocation.lat, this.userLocation.lng, s.lat, s.lng);
            }
          });

          this.filters.sortBy = 'user';
          this.applyFilters();

          // Smooth 3D flyTo user
          this.map.flyTo({
            center: [this.userLocation.lng, this.userLocation.lat],
            zoom: 13.5,
            pitch: 25,
            speed: 1.2
          });
        },
        (err) => {
          this.geoBtn.textContent = '📍 Near Me';
          alert('Unable to retrieve your location. Please check browser permissions.');
        },
        { timeout: 8000 }
      );
    }

    haversine(lat1, lon1, lat2, lon2) {
      const R = 6371;
      const dLat = (lat2 - lat1) * Math.PI / 180;
      const dLon = (lon2 - lon1) * Math.PI / 180;
      const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
                Math.sin(dLon / 2) * Math.sin(dLon / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      return Math.round(R * c * 10) / 10;
    }

    applyFilters() {
      const q = this.filters.query;
      const z = this.filters.zone;
      const l = this.filters.level;

      this.filteredSchools = this.schools.filter((s) => {
        // Query match
        if (q) {
          const matchName = s.name.toLowerCase().includes(q);
          const matchPostal = s.postal_code.includes(q);
          const matchAddress = s.address && s.address.toLowerCase().includes(q);
          const matchArea = s.area && s.area.toLowerCase().includes(q);
          const matchMrt = s.mrt && s.mrt.toLowerCase().includes(q);
          if (!matchName && !matchPostal && !matchAddress && !matchArea && !matchMrt) {
            return false;
          }
        }

        // Zone filter
        if (z !== 'ALL' && s.zone.toUpperCase() !== z.toUpperCase()) {
          return false;
        }

        // Level filter
        if (l !== 'ALL') {
          if (l === 'PRIMARY' && s.level_group !== 'Primary') return false;
          if (l === 'SECONDARY' && s.level_group !== 'Secondary') return false;
          if (l === 'JC' && s.level_group !== 'Pre-University / JC') return false;
          if (l === 'MIXED' && s.level_group !== 'Mixed Level') return false;
        }

        // Uniform availability filter
        if (this.filters.stock === 'STOCKED' && !s.shop) return false;

        return true;
      });

      // Sorting
      if (this.filters.sortBy === 'user' && this.userLocation) {
        this.filteredSchools.sort((a, b) => (a.distance_to_user_km || 999) - (b.distance_to_user_km || 999));
      } else {
        this.filteredSchools.sort((a, b) => (a.distance_to_showroom_km || 999) - (b.distance_to_showroom_km || 999));
      }

      // Update Count
      if (this.countEl) {
        this.countEl.innerHTML = `Showing <strong>${this.filteredSchools.length}</strong> of ${this.schools.length} schools`;
      }
      if (this.mobileCount) {
        this.mobileCount.textContent = this.filteredSchools.length;
      }

      // Render Markers on MapLibre
      if (this.map && this.map.isStyleLoaded()) {
        this.renderMarkers();
      }

      // Render Sidebar List
      this.renderList();
    }

    renderMarkers() {
      if (!this.map) return;

      // Remove existing school markers
      this.markersMap.forEach(({ marker }) => marker.remove());
      this.markersMap.clear();

      this.filteredSchools.forEach((school) => {
        if (!school.lat || !school.lng) return;

        let badgeClass = 'ds-osm-marker--primary';
        let iconSymbol = '🎓';
        if (school.level_group === 'Secondary') {
          badgeClass = 'ds-osm-marker--secondary';
        } else if (school.level_group === 'Pre-University / JC') {
          badgeClass = 'ds-osm-marker--jc';
        } else if (school.level_group === 'Mixed Level') {
          badgeClass = 'ds-osm-marker--mixed';
        }

        // Create marker DOM element (hotelist style)
        const el = document.createElement('div');
        el.className = `ds-osm-marker ${badgeClass}${school.shop ? ' ds-osm-marker--stocked' : ''}`;
        el.innerHTML = `<span style="font-size:12px;">${iconSymbol}</span>`;
        el.setAttribute('data-school-id', school.id);
        el.setAttribute('title', school.name);

        const popup = new maplibregl.Popup({
          offset: 25,
          closeButton: true,
          closeOnClick: false,
          maxWidth: '340px'
        }).setHTML(this.createPopupContent(school));

        popup.on('close', () => {
          if (this.selectedSchoolId === school.id) {
            this.selectedSchoolId = null;
            this.clearSchoolRadiusCircle();
            marker.getElement().classList.remove('is-active');
            if (this.listEl) {
              const card = this.listEl.querySelector(`.ds-school-card[data-school-id="${school.id}"]`);
              if (card) card.classList.remove('is-selected');
            }
          }
        });

        const marker = new maplibregl.Marker({ element: el })
          .setLngLat([school.lng, school.lat])
          .addTo(this.map);

        el.addEventListener('click', (e) => {
          e.stopPropagation();
          e.preventDefault();
          this.selectSchool(school.id, true);
        });

        // Hover over pin: instantly previews dark product card & 1km radius (if no pin is locked)
        el.addEventListener('mouseenter', () => {
          if (window.innerWidth > 768) {
            this.hoverSchool(school.id, true);
          }
        });

        this.markersMap.set(school.id, { marker, popup, data: school });
      });
    }

    createPopupContent(school) {
      let levelBadgeClass = 'ds-school-popup-badge--primary';
      let levelBadgeStyle = 'background:#1e40af;color:#dbeafe;';
      if (school.level_group === 'Secondary') {
        levelBadgeClass = 'ds-school-popup-badge--secondary';
        levelBadgeStyle = 'background:#065f46;color:#a7f3d0;';
      } else if (school.level_group === 'Pre-University / JC') {
        levelBadgeClass = 'ds-school-popup-badge--jc';
        levelBadgeStyle = 'background:#5b21b6;color:#ede9fe;';
      } else if (school.level_group === 'Mixed Level') {
        levelBadgeClass = 'ds-school-popup-badge--mixed';
        levelBadgeStyle = 'background:#92400e;color:#fef3c7;';
      }

      const distText = school.distance_to_user_km !== undefined
        ? `${school.distance_to_user_km} km from you`
        : `${school.distance_to_showroom_km} km to AMK Showroom`;

      const safeWebsite = sanitizeUrl(school.website);
      const cleanPhone = SHOWROOM.phone.replace(/\s+/g, '');

      return `
        <div class="ds-school-popup-card">
          <div class="ds-school-popup-card__header">
            <div class="ds-school-popup-card__eyebrow">
              <span class="ds-school-popup-badge ${levelBadgeClass}" style="${levelBadgeStyle}">${escapeHtml(school.level_group)}</span>
              <span class="ds-school-popup-badge" style="background:rgba(255,255,255,0.12);color:#e2e8f0;">${escapeHtml(school.zone)} ZONE</span>
              ${school.shop ? `
                <span class="ds-school-popup-badge ds-school-popup-badge--stocked">✔ Uniform In Store</span>
              ` : ''}
            </div>
            <h4 class="ds-school-popup-card__title">${escapeHtml(school.name)}</h4>
            <div class="ds-school-popup-card__address">📍 ${escapeHtml(school.address)}, Singapore ${escapeHtml(school.postal_code)}</div>
          </div>

          <div class="ds-school-popup-card__actions ds-school-popup-card__actions--shop">
            ${school.shop ? `
              <a
                href="${escapeHtml(school.shop.url)}"
                class="ds-school-popup-btn ds-school-popup-btn--product"
                data-collection-handle="${escapeHtml(school.shop.handle)}"
                aria-label="Shop ${escapeHtml(school.name)} uniform, ${Number(school.shop.count) || 0} items"
              >
                🛒 Shop School Uniform (${Number(school.shop.count) || 0} items)
              </a>
            ` : `
              <p class="ds-school-popup-unstocked">Uniform not available online yet.</p>
              <a
                href="tel:${cleanPhone}"
                class="ds-school-popup-btn ds-school-popup-btn--portal"
                aria-label="Call our showroom about ${escapeHtml(school.name)} uniforms"
              >
                📞 Ask Our Showroom
              </a>
            `}
          </div>

          <div class="ds-school-popup-card__metrics">
            <div class="ds-school-popup-metric-row">
              <span class="ds-school-popup-metric-label">School Type:</span>
              <span class="ds-school-popup-metric-value">${escapeHtml(school.type || 'Government School')}</span>
            </div>
            ${school.nature ? `
              <div class="ds-school-popup-metric-row">
                <span class="ds-school-popup-metric-label">School Nature:</span>
                <span class="ds-school-popup-metric-value">${escapeHtml(school.nature)}</span>
              </div>
            ` : ''}
            <div class="ds-school-popup-metric-row">
              <span class="ds-school-popup-metric-label">Distance to our AMK Showroom:</span>
              <span class="ds-school-popup-metric-value ds-school-popup-metric-value--highlight">${escapeHtml(distText)}</span>
            </div>
            ${school.mrt ? `
              <div class="ds-school-popup-metric-row">
                <span class="ds-school-popup-metric-label">Nearest MRT:</span>
                <span class="ds-school-popup-metric-value">${escapeHtml(school.mrt.split(',')[0])}</span>
              </div>
            ` : ''}
          </div>

          ${school.website ? `
            <div class="ds-school-popup-card__actions">
              <a href="${safeWebsite}" target="_blank" rel="noopener" class="ds-school-popup-btn ds-school-popup-btn--portal">
                🌐 Official MOE School Portal ↗
              </a>
            </div>
          ` : ''}

          <div class="ds-school-popup-card__footer">
            <span>Singapore Open Data Licence v1.0</span>
          </div>
        </div>
      `;
    }

    renderList() {
      if (!this.listEl) return;

      if (this.filteredSchools.length === 0) {
        this.listEl.innerHTML = `
          <div class="ds-school-empty">
            <div class="ds-school-empty__icon">🔍</div>
            <p style="font-weight:700;margin:0 0 4px 0;">No schools matched your search</p>
            <p style="font-size:13px;margin:0;">Try adjusting your keywords or clearing the filters.</p>
          </div>
        `;
        return;
      }

      const html = this.filteredSchools.map((s) => {
        const isSelected = s.id === this.activeSchoolId;
        let badgeClass = 'ds-school-badge--primary';
        if (s.level_group === 'Secondary') badgeClass = 'ds-school-badge--secondary';
        else if (s.level_group === 'Pre-University / JC') badgeClass = 'ds-school-badge--jc';
        else if (s.level_group === 'Mixed Level') badgeClass = 'ds-school-badge--mixed';

        const distText = s.distance_to_user_km !== undefined
          ? `${s.distance_to_user_km} km from you`
          : `${s.distance_to_showroom_km} km to AMK Showroom`;

        const safeWebsite = sanitizeUrl(s.website);

        return `
          <article
            class="ds-school-card ${isSelected ? 'is-selected' : ''}"
            data-school-id="${s.id}"
            data-stocked="${s.shop ? 'true' : 'false'}"
            ${s.shop ? `data-collection-handle="${escapeHtml(s.shop.handle)}"` : ''}
          >
            <div class="ds-school-card__top">
              <h4 class="ds-school-card__name">${escapeHtml(s.name)}</h4>
              <span class="ds-school-badge ${badgeClass}">${escapeHtml(s.level_group)}</span>
            </div>
            <p class="ds-school-card__address">${escapeHtml(s.address)}, S(${escapeHtml(s.postal_code)})</p>
            <div class="ds-school-card__meta">
              <span class="ds-meta-pill ds-distance-pill">📍 ${escapeHtml(distText)}</span>
              ${s.mrt ? `<span class="ds-meta-pill">🚇 ${escapeHtml(s.mrt.split(',')[0])}</span>` : ''}
              <span class="ds-meta-pill">${escapeHtml(s.zone)}</span>
              ${s.shop ? `<span class="ds-meta-pill ds-stock-pill">✔ Uniform in store</span>` : ''}
            </div>
            ${s.shop || s.website ? `
              <div class="ds-school-card__action">
                ${s.shop ? `
                  <a
                    href="${escapeHtml(s.shop.url)}"
                    class="ds-card-shop-btn"
                    aria-label="Shop ${escapeHtml(s.name)} uniform"
                    onclick="event.stopPropagation()"
                  >
                    🛒 Shop Uniform
                  </a>
                ` : ''}
                ${s.website ? `
                  <a href="${safeWebsite}" target="_blank" rel="noopener" class="ds-card-link" onclick="event.stopPropagation()">
                    🌐 Official MOE Portal ↗
                  </a>
                ` : ''}
              </div>
            ` : ''}
          </article>
        `;
      }).join('');

      this.listEl.innerHTML = html;

      // Add click & hover listeners to cards
      let hoverDebounce;
      this.listEl.querySelectorAll('.ds-school-card').forEach((card) => {
        const id = parseInt(card.dataset.schoolId, 10);

        // Click to lock selection & full camera flight
        card.addEventListener('click', () => {
          this.selectSchool(id, true);
        });

        // Hover over card: previews popup on map only if no school is actively pinned
        card.addEventListener('mouseenter', () => {
          if (window.innerWidth > 768 && !this.selectedSchoolId) {
            clearTimeout(hoverDebounce);
            hoverDebounce = setTimeout(() => {
              this.hoverSchool(id, false);
            }, 60);
          }
        });
      });
    }

    /**
     * Master-Detail Hover Synchronization
     * Highlights card, scales map marker, opens dark product card, and draws 1km MOE priority circle.
     */
    hoverSchool(id, fromMap = false) {
      if (window.innerWidth <= 768) return;
      this.hoveredSchoolId = id;

      // If a school was explicitly clicked & pinned, do not override its card on casual hover
      if (this.selectedSchoolId && this.selectedSchoolId !== id) {
        if (!fromMap && this.listEl) {
          const hoveredCard = this.listEl.querySelector(`.ds-school-card[data-school-id="${id}"]`);
          if (hoveredCard) hoveredCard.classList.add('is-hovered');
        }
        return;
      }

      // 1. Highlight in sidebar list (without auto-scrolling)
      if (this.listEl) {
        this.listEl.querySelectorAll('.ds-school-card').forEach((card) => {
          const isTarget = parseInt(card.dataset.schoolId, 10) === id;
          const isSelected = parseInt(card.dataset.schoolId, 10) === this.selectedSchoolId;
          card.classList.toggle('is-selected', isTarget || isSelected);
          card.classList.toggle('is-hovered', isTarget);
        });
      }

      // 2. Highlight marker on map & open popup card
      const item = this.markersMap.get(id);
      if (item && this.map) {
        // Active visual state for marker
        this.markersMap.forEach(({ marker }) => {
          marker.getElement().classList.remove('is-active');
        });
        if (this.showroomMarker) {
          this.showroomMarker.getElement().classList.remove('is-active');
        }
        item.marker.getElement().classList.add('is-active');

        // Draw MOE P1 Priority Circles (strictly for Primary Schools)
        this.updateSchoolRadiusCircle(item.data);

        // Check if marker is in current map view; if not, pan gently
        const bounds = this.map.getBounds();
        const coords = [item.data.lng, item.data.lat];
        if (!bounds.contains(coords)) {
          this.map.easeTo({
            center: coords,
            duration: 350
          });
        }

        // Close Showroom popup if open
        if (this.showroomPopup && this.showroomPopup.isOpen()) {
          this.showroomPopup.remove();
        }

        // Open dark pin card
        if (!item.popup.isOpen()) {
          this.markersMap.forEach(({ popup }) => {
            if (popup.isOpen()) popup.remove();
          });
          item.popup.setLngLat([item.data.lng, item.data.lat]).addTo(this.map);
        }
      }
    }

    selectSchool(id, panMap = true) {
      this.selectedSchoolId = id;
      this.hoveredSchoolId = id;
      this.activeSchoolId = id;
      const isMobile = window.innerWidth <= 768;

      // If selected while in mobile list view, switch back to map view
      if (isMobile && this.isMobileListView) {
        this.toggleMobileView();
      }

      // Highlight in sidebar list
      if (this.listEl) {
        this.listEl.querySelectorAll('.ds-school-card').forEach((card) => {
          card.classList.toggle('is-selected', parseInt(card.dataset.schoolId, 10) === id);
          card.classList.remove('is-hovered');
        });

        const activeCard = this.listEl.querySelector(`.ds-school-card[data-school-id="${id}"]`);
        if (activeCard) {
          activeCard.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }

      // Highlight active marker on map
      this.markersMap.forEach(({ marker }) => {
        marker.getElement().classList.remove('is-active');
      });
      if (this.showroomMarker) {
        this.showroomMarker.getElement().classList.remove('is-active');
      }

      // Fly to marker on MapLibre & open popup or bottom sheet
      const item = this.markersMap.get(id);
      if (item && this.map) {
        item.marker.getElement().classList.add('is-active');

        // Update JSON-LD structured data for autonomous agents & crawlers
        this.updateSchoolStructuredData(item.data);

        // Draw MOE P1 Priority Circles (strictly for Primary Schools)
        this.updateSchoolRadiusCircle(item.data);

        const isPrimary = item.data.level_group === 'Primary';

        if (isMobile) {
          if (item.popup && item.popup.isOpen()) {
            item.popup.remove();
          }

          if (this.mobileSheet && this.mobileSheetContent) {
            this.mobileSheetContent.innerHTML = this.createPopupContent(item.data);
            this.mobileSheet.classList.add('is-open');
            this.mobileSheet.setAttribute('aria-hidden', 'false');
          }

          this.map.flyTo({
            center: [item.data.lng, item.data.lat],
            offset: [0, -80],
            zoom: isPrimary ? 13.8 : 15.0,
            pitch: 20,
            speed: 1.2,
            curve: 1.4,
            essential: true
          });
        } else {
          if (this.mobileSheet) {
            this.closeMobileSheet();
          }

          // Close Showroom popup if open
          if (this.showroomPopup && this.showroomPopup.isOpen()) {
            this.showroomPopup.remove();
          }
          // Close other school popups
          this.markersMap.forEach(({ popup }) => {
            if (popup && popup.isOpen() && popup !== item.popup) {
              popup.remove();
            }
          });

          // Unconditionally show and pin the card attached to this school location
          item.popup.setLngLat([item.data.lng, item.data.lat]).addTo(this.map);

          if (panMap) {
            // Smooth 3D camera flight: wide framing for primary schools with 1km & 2km rings
            this.map.flyTo({
              center: [item.data.lng, item.data.lat],
              zoom: isPrimary ? 14.2 : 15.2,
              pitch: 28,
              speed: 1.2,
              curve: 1.4,
              essential: true
            });
          }
        }
      }
    }

    clearSelection() {
      this.selectedSchoolId = null;
      this.hoveredSchoolId = null;
      this.activeSchoolId = null;
      this.clearSchoolRadiusCircle();
      this.markersMap.forEach(({ marker, popup }) => {
        marker.getElement().classList.remove('is-active');
        if (popup.isOpen()) popup.remove();
      });
      if (this.showroomPopup && this.showroomPopup.isOpen()) {
        this.showroomPopup.remove();
      }
      if (this.showroomMarker) {
        this.showroomMarker.getElement().classList.remove('is-active');
      }
      if (this.listEl) {
        this.listEl.querySelectorAll('.ds-school-card').forEach((card) => {
          card.classList.remove('is-selected', 'is-hovered');
        });
      }
    }

    /**
     * Updates dynamic Schema.org School structured data for autonomous agents & crawlers
     */
    updateSchoolStructuredData(school) {
      if (!school) return;
      let el = document.getElementById('ds-active-school-schema');
      if (!el) {
        el = document.createElement('script');
        el.type = 'application/ld+json';
        el.id = 'ds-active-school-schema';
        document.head.appendChild(el);
      }
      const schemaObj = {
        '@context': 'https://schema.org',
        '@type': 'School',
        'name': school.name,
        'address': {
          '@type': 'PostalAddress',
          'streetAddress': school.address,
          'postalCode': school.postal_code,
          'addressCountry': 'SG'
        },
        'geo': {
          '@type': 'GeoCoordinates',
          'latitude': school.lat,
          'longitude': school.lng
        }
      };
      if (school.website) schemaObj.url = school.website;
      if (school.telephone) schemaObj.telephone = school.telephone;
      if (school.shop && school.shop.url) {
        schemaObj.hasOfferCatalog = {
          '@type': 'OfferCatalog',
          'name': `${school.name} Uniform Collection`,
          'url': school.shop.url
        };
      }
      el.textContent = JSON.stringify(schemaObj);
    }

    toggleMobileView() {
      this.isMobileListView = !this.isMobileListView;
      this.container.classList.toggle('is-list-active', this.isMobileListView);

      if (this.mobileToggle) {
        const toggleIcon = this.mobileToggle.querySelector('.ds-mobile-view-toggle__icon');
        const toggleText = this.mobileToggle.querySelector('.ds-mobile-view-toggle__text');

        if (this.isMobileListView) {
          if (toggleIcon) toggleIcon.textContent = '🗺️';
          if (toggleText) toggleText.textContent = 'View Map';
          this.closeMobileSheet();
        } else {
          if (toggleIcon) toggleIcon.textContent = '📋';
          if (toggleText) toggleText.innerHTML = `View List (<span id="ds-mobile-count">${this.filteredSchools.length}</span>)`;
          setTimeout(() => {
            if (this.map) this.map.resize();
          }, 120);
        }
      }
    }

    closeMobileSheet() {
      if (this.mobileSheet) {
        this.mobileSheet.classList.remove('is-open');
        this.mobileSheet.setAttribute('aria-hidden', 'true');
      }
      this.activeSchoolId = null;
      if (this.listEl) {
        this.listEl.querySelectorAll('.ds-school-card').forEach((card) => {
          card.classList.remove('is-selected');
        });
      }
    }
  }

  // Initialize on DOM Ready
  function initSchoolLocators() {
    document.querySelectorAll('[data-section-type="ds-school-osm-map"]').forEach((el) => {
      new SchoolMapLocator(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSchoolLocators);
  } else {
    initSchoolLocators();
  }
})();
